'use client';

import { FormEvent, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AuthFormWrapper } from '@components/AuthForm';
import { Button } from '@components/Button';
import { Input } from '@components/Input';

import { type FormData } from './interfaces';

interface AuthFormProps {
  onSubmit: (data: FormData) => void;
  hasError: boolean;
  labelButton: string;
  requiresEmailConfirmation?: boolean;
  children?: React.ReactNode;
}

/**
 * AuthForm component renders a form for user authentication.
 * It includes fields for email and password, and a submit button.
 */
export function AuthForm({
  onSubmit,
  hasError,
  labelButton,
  requiresEmailConfirmation = false,
  children,
}: AuthFormProps) {
  const formRef = useRef<HTMLFormElement>(null);
  const { t } = useTranslation('common');
  const [isButtonClick, setIsButtonClick] = useState(false);
  const [emailConfirmationError, setEmailConfirmationError] = useState('');

  const handleFormSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (formRef.current) {
      const formData = new FormData(formRef.current);
      const email = ((formData.get('email') as string) || '').trim();
      const emailConfirmation = ((formData.get('emailConfirmation') as string) || '').trim();

      const data: FormData = {
        email,
        emailConfirmation: requiresEmailConfirmation ? emailConfirmation : undefined,
        password: (formData.get('password') as string) || '',
      };

      if (!isButtonClick) {
        return;
      }

      if (requiresEmailConfirmation && email.toLowerCase() !== emailConfirmation.toLowerCase()) {
        setEmailConfirmationError(t('errorEmailMismatch'));
        setIsButtonClick(false);
        return;
      }

      setEmailConfirmationError('');
      onSubmit(data);
      setIsButtonClick(false);
    }
  };

  return (
    <AuthFormWrapper onSubmit={handleFormSubmit} ref={formRef} autoComplete="off">
      <Input
        type="email"
        id="email"
        placeholder={t('enterEmail')}
        name="email"
        label={t('email')}
        hasError={hasError}
        required
      />
      {requiresEmailConfirmation && (
        <Input
          type="email"
          id="emailConfirmation"
          placeholder={t('enterConfirmEmail')}
          name="emailConfirmation"
          label={t('confirmEmail')}
          hasError={emailConfirmationError.length > 0}
          errorLabel={emailConfirmationError}
          required
        />
      )}
      <Input
        type="password"
        id="password"
        placeholder={t('enterPassword')}
        name="password"
        label={t('password')}
        hasError={hasError}
        required
      />
      {children}
      <Button
        type="submit"
        label={labelButton}
        variant={'primary'}
        onClick={() => setIsButtonClick(true)}
      />
    </AuthFormWrapper>
  );
}
